import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { CustomValidatorsService } from '@app/@shared/custom-validators/custom-validators.service';
import { ResponseDialogComponent } from '@app/@shared/dialogs/response/response-dialog.component';
import { ControlPanelStore } from '@app/control-panel/control-panel-service/control-panel.store';
import { CredentialsService } from '@app/login';
import { CommonDataDTO } from '@app/requests/model/combos-model';
import { TranslateService } from '@ngx-translate/core';
import FileSaver from 'file-saver';
import { TpvDetailOperation, TpvQueryResponse, TPVTypeSelected } from '../model/revolving-model';
import { RevolvingStore } from '../services/revolving.store';
import { LoaderGenericService } from '@app/@shared/loader-generic/loader-generic.service';
import { CombosStore } from '@app/requests/combos-service/combos.store';
import { ExcelService } from '@app/@shared/services/print/excel.service';
import { Credentials } from '@app/@core/models/login-models';
import {
  CHAIN,
  MACRO_CHAIN,
  MACRO_OFFICE,
  OFFICE_DIRECTOR,
  BUSINESS_DIRECTOR,
  PRESCRIBER,
} from '@app/@core/constants/users.constants';
import { DatepickerUtilsService } from '@app/@shared/services/utils/datepicker-utils.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LOCALE_ES } from '@app/@core/constants/common.constants';
import { MatSelectChange } from '@angular/material/select';

const MAX_NUM_DATA = 100;

@UntilDestroy()
@Component({
  selector: 'app-queries',
  templateUrl: './queries.component.html',
  styleUrls: ['./queries.component.scss'],
})
export class QueriesComponent implements OnInit {
  queryTypes: CommonDataDTO[] = [];
  displayedColumns: string[] = [
    'date',
    'contract',
    'nifnie',
    'customerName',
    'import',
    'type',
    'term',
    'expenses',
    'netAmount',
    'liquidationDate',
    'description',
    'action',
  ];

  public head = [
    'queries.header.date',
    'queries.header.contract',
    'queries.header.nifnie',
    'queries.header.customerName',
    'queries.header.import',
    'queries.header.type',
    'queries.header.term',
    'queries.header.expenses',
    'queries.header.netAmount',
    'queries.header.liquidationDate',
    'queries.header.description',
  ];

  userInfo: Credentials;

  macroChainList: CommonDataDTO[] = [];
  macroOfficeList: CommonDataDTO[] = [];
  optionsList: CommonDataDTO[] = [];
  liquidationsList: CommonDataDTO[] = [];

  state: CommonDataDTO;

  searchButtonEnabled: boolean;
  isLoading: boolean;
  showSelectOption: boolean = false;
  showDetail: boolean = false;
  showGroupDetail: boolean = false;
  showGroupOption: boolean = false;

  isMacroOffices: boolean = false;
  isMacroChains: boolean = false;
  isBussinesDirector: boolean = false;
  isPrescriber: boolean = false;
  isSelectedOption: boolean = false;
  disableSearch: boolean = false;

  currentDate: Date = new Date();
  minFinalDate: Date = new Date();

  pagination: Map<string, string>;
  tpvQueryResponse: TpvQueryResponse;

  public revolvingQueriesForm: FormGroup;

  dataSource = new MatTableDataSource<TpvDetailOperation>();

  constructor(
    private formBuilder: FormBuilder,
    private controlPanelStore: ControlPanelStore,
    private revolvingStore: RevolvingStore,
    private credentialsService: CredentialsService,
    private dialog: MatDialog,
    private customValidators: CustomValidatorsService,
    private translateService: TranslateService,
    private loaderGenericservice: LoaderGenericService,
    private combosStore: CombosStore,
    private datepickerUtils: DatepickerUtilsService,
    private excelService: ExcelService
  ) {}

  ngOnInit(): void {
    let dataString: string = localStorage.getItem('credentials');
    this.userInfo = JSON.parse(dataString);

    if (this.userInfo.userType == MACRO_OFFICE) this.isMacroOffices = true;

    if (this.userInfo.userType == MACRO_CHAIN) this.isMacroChains = true;

    if (this.userInfo.userType == BUSINESS_DIRECTOR) this.isBussinesDirector = true;

    if (this.userInfo.userType == PRESCRIBER) this.isPrescriber = true;

    this.revolvingQueriesForm = this.formBuilder.group({
      queryType: ['', Validators.required],
      selectOption: ['', Validators.required],
      radioButtonDetail: [''],
      liquidations: ['', Validators.required],
      initialDate: ['', [Validators.required, this.customValidators.checkIsFutureDate]],
      finalDate: [{ value: '', disabled: true }, [Validators.required, this.customValidators.checkIsFutureDate]],
    });

    if (this.isMacroChains || this.isBussinesDirector) {
      this.revolvingQueriesForm.controls.queryType?.disable();
      this.revolvingQueriesForm.addControl('macroChain', new FormControl({ value: '' }, Validators.required));
      this.revolvingQueriesForm.controls.macroChain.valueChanges.subscribe(() => this.onChangeMacroChain());
      this.getMacroChains();
    }

    if (this.isMacroOffices || this.isBussinesDirector) {
      this.revolvingQueriesForm.controls.queryType?.disable();
      this.revolvingQueriesForm.addControl('macroOffice', new FormControl({ value: '' }, Validators.required));
      this.revolvingQueriesForm.controls.macroOffice.valueChanges
        .pipe(untilDestroyed(this))
        .subscribe(() => this.onChangeMacroOffice());
      this.getMacroOffices();
    }
    this.translateService.currentLang === LOCALE_ES ? this.datepickerUtils.spanish() : this.datepickerUtils.catalan();
    this.translateService.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe((event) =>
        event.lang === LOCALE_ES ? this.datepickerUtils.spanish() : this.datepickerUtils.catalan()
      );

    this.revolvingQueriesForm.controls.queryType.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value) => (value ? this.onChangeQueryType() : null));

    this.revolvingQueriesForm.controls.initialDate.statusChanges.subscribe((value) => {
      if (value === 'VALID') {
        const initialDateValue = this.revolvingQueriesForm.controls.initialDate.value;
        this.revolvingQueriesForm.controls.finalDate.enable();
        this.minFinalDate = initialDateValue.toDate();
      } else {
        this.revolvingQueriesForm.controls.finalDate.reset();
        this.revolvingQueriesForm.controls.finalDate.disable();
      }
    });

    this.getTypes();
    this.getOptions();
  }

  selectOptionRadioButton() {
    this.isSelectedOption = true;
  }

  checkDate() {
    if (
      this.revolvingQueriesForm.controls.finalDate.value &&
      this.revolvingQueriesForm.controls.initialDate.value &&
      this.revolvingQueriesForm.controls.initialDate.value > this.revolvingQueriesForm.controls.finalDate.value
    ) {
      this.revolvingQueriesForm.controls.finalDate.setErrors({ lowerDate: true });
    }
  }

  consult() {
    this.pagination = new Map<string, string>();
    this.pagination.set('0', '');
    const keys = [...this.pagination.keys()];
    this.dataSource.data = [];
    this.revolvingStore
      .sendQuery(this.revolvingQueriesForm, this.credentialsService.credentials.userId, '')
      .then((data) => {
        this.insertDatasourceData(data, keys);
      })
      .catch((error) => console.error(error));
  }

  onChangeQueryType() {
    this.loaderGenericservice.showSpinner();
    //se activa el otro combo
    this.showSelectOption = true;
    //se muestran los radiobutton correspondientes a la seleccion
    if (this.revolvingQueriesForm.controls.queryType.value === TPVTypeSelected.group) {
      this.showDetail = true;
      this.isSelectedOption = false;
      this.showGroupOption = true;
      this.getGroups();
    } else if (this.revolvingQueriesForm.controls.queryType.value === TPVTypeSelected.prescriptor) {
      this.showDetail = true;
      this.isSelectedOption = true;
      this.showGroupOption = false;
      this.getPrescriptors();
    } else if (this.revolvingQueriesForm.controls.queryType.value === TPVTypeSelected.establishment) {
      this.showDetail = false;
      this.isSelectedOption = false;
      this.getEstablishments();
    }

    if (this.isPrescriber) {
      if (this.revolvingQueriesForm.controls.queryType.value === TPVTypeSelected.group) {
        this.revolvingQueriesForm.controls.selectOption.disable();
        this.disableSearch = true;
      } else {
        this.revolvingQueriesForm.controls.selectOption.enable();
        this.disableSearch = false;
      }
    }
  }

  onChangeMacroOffice() {
    this.revolvingQueriesForm.controls.queryType?.setValue(null);
    this.optionsList = null;

    if (this.revolvingQueriesForm.controls.macroOffice.value) {
      this.revolvingQueriesForm.controls.queryType?.enable();

      this.revolvingQueriesForm.controls.macroChain?.disable();
      this.revolvingQueriesForm.controls.macroChain?.setValidators(null);
    } else {
      this.revolvingQueriesForm.controls.queryType?.disable();
      this.showSelectOption = false;

      this.revolvingQueriesForm.controls.macroOffice.setValue(null);
      this.revolvingQueriesForm.controls.macroChain?.setValidators([Validators.required]);
      this.revolvingQueriesForm.controls.macroChain?.enable();
    }
  }

  onChangeMacroChain() {
    this.revolvingQueriesForm.controls.queryType?.setValue('');
    this.optionsList = [];

    if (this.revolvingQueriesForm.controls.macroChain.value) {
      this.revolvingQueriesForm.controls.queryType?.enable();

      this.revolvingQueriesForm.controls.macroOffice?.disable();
      this.revolvingQueriesForm.controls.macroOffice?.setValidators(null);
    } else {
      this.revolvingQueriesForm.controls.queryType?.disable();
      this.showSelectOption = false;

      this.revolvingQueriesForm.controls.macroChain.setValue(null);
      this.revolvingQueriesForm.controls.macroOffice?.setValidators([Validators.required]);
      this.revolvingQueriesForm.controls.macroOffice?.enable();
    }
  }

  selectRequest(event: MatSelectChange) {
    if (event) {
      if (event.value === 'C' || event.value === 'P') {
        this.isSelectedOption = false;
      } else {
        this.isSelectedOption = true;
      }
    }
  }

  createPdf() {
    this.revolvingStore
      .printQuery(this.revolvingQueriesForm, this.credentialsService.credentials.userId, 'pdf')
      .then((data) => {
        if (data) {
          const date = new Date();
          let file = new Blob([data], { type: 'application/pdf' });
          FileSaver.saveAs(
            file,
            'ConsultaDetalleTarjeta_' + date.getUTCDate() + date.getUTCMonth() + date.getUTCFullYear() + '.pdf'
          );
        }
      })
      .catch((error) => console.error(error));
  }

  createExcel() {
    let dataForExcel: any[] = [];

    this.dataSource.data.forEach((row: TpvDetailOperation) => {
      const { strIdEstablecimiento, trIdPrescriptor, strNuRefere, strApell1, strApell2, ...data } = row;
      const dataExcelOrder = JSON.stringify(data, [
        'strFecha',
        'strNumCon',
        'strDniCif',
        'strNombre',
        'dblImModLoc',
        'strTipOper',
        'strNuCuotas',
        'dblGastos',
        'dblImpNeto',
        'strFechaLiq',
        'strDescripcion',
      ]);
      const ObjectDataExcel = JSON.parse(dataExcelOrder);
      dataForExcel.push(Object.values(ObjectDataExcel));
    });

    const excelData = {
      fileName: this.translateService.instant('queries.excel.name'),
      state: this.optionsList[0].id !== '' ? this.optionsList[0].value + '(' + this.optionsList[0].id + ')' : '',
      headers: this.head.map((header) => this.translateService.instant(header)),
      data: dataForExcel,
    };

    this.excelService.exportExcel(excelData, this.translateService.instant('queries.excel.name'));
  }

  onClickPrintQuery(element: TpvDetailOperation) {
    console.log(element);
    this.revolvingStore
      .printQueryBoleta(element, this.credentialsService.credentials.userId)
      .then((data) => {
        if (data) {
          const date = new Date();
          let file = new Blob([data], { type: 'application/pdf' });
          FileSaver.saveAs(
            file,
            'Boleta_' +
              element.strNuRefere +
              '_' +
              date.getUTCDate() +
              date.getUTCMonth() +
              date.getUTCFullYear() +
              '.pdf'
          );
        }
      })
      .catch((error) => console.error(error));
  }

  onClickPreviousPage() {
    const keys = [...this.pagination.keys()];
    for (let i = 1; i <= 2; i++) {
      this.pagination.delete(keys[keys.length - 1]);
      keys.pop();
    }
    this.dataSource.data = [];
    this.revolvingStore
      .sendQuery(
        this.revolvingQueriesForm,
        this.credentialsService.credentials.userId,
        this.pagination.get(keys[keys.length - 1])
      )
      .then((data) => {
        this.insertDatasourceData(data, keys);
      })
      .catch((error) => console.error(error));
  }

  onClickNextPage() {
    const keys = [...this.pagination.keys()];
    this.dataSource.data = [];
    this.revolvingStore
      .sendQuery(
        this.revolvingQueriesForm,
        this.credentialsService.credentials.userId,
        this.pagination.get(keys[keys.length - 1])
      )
      .then((data) => {
        this.insertDatasourceData(data, keys);
      })
      .catch((error) => console.error(error));
  }

  hasAnyOperation(): boolean {
    return this.dataSource.data.length > 0;
  }

  isPreviousButtonDisplayed() {
    const keys = [...this.pagination.keys()];
    return keys?.length > 2;
  }

  isNextButtonDisplayed() {
    return (
      this.tpvQueryResponse.objOperacionTarjetaBean[0].objDetalleOperacionTarjetaBean.length === MAX_NUM_DATA &&
      this.tpvQueryResponse.strClavePag
    );
  }

  private insertDatasourceData(data: TpvQueryResponse, keys: string[]) {
    if (data && Object.keys(data).length > 0) {
      this.tpvQueryResponse = data;
      this.tpvQueryResponse.objOperacionTarjetaBean.forEach((operationDetailUser) => {
        operationDetailUser.objDetalleOperacionTarjetaBean.forEach((detailOperation) => {
          detailOperation.strNombre =
            detailOperation.strNombre + ' ' + detailOperation.strApell1 + ' ' + detailOperation.strApell2;
          this.dataSource.data.push(detailOperation);
        });
      });
      this.pagination.set(keys?.length.toString(), data.strClavePag);
    } else {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.width = '100%';
      dialogConfig.maxWidth = '500px';
      //TOOD-Jed: Comprobar parametros
      dialogConfig.data = {
        title: 'queries.dialog.response.title',
        text: this.translateService.instant('queries.dialog.response.error.data'),
        buttonCancel: false,
        noRedirect: true,
      };
      dialogConfig.disableClose = true;

      this.dialog.open(ResponseDialogComponent, dialogConfig);
    }
  }

  private getGroups() {
    return this.combosStore.getChains(this.getFilter()).then(
      (data) => {
        this.loaderGenericservice.stopSpinner();
        if (data) {
          this.optionsList = data;

          if (this.optionsList.length === 1) {
            this.revolvingQueriesForm.controls.selectOption.setValue(this.optionsList[0].id);
          }

          this.optionsList.unshift({ id: '', value: '' });
        }
      },
      (error) => {
        this.loaderGenericservice.stopSpinner();
        console.error(error);
      }
    );
  }

  private getPrescriptors() {
    return this.combosStore
      .getPrescribers(this.getFilter())
      .then((data) => {
        this.loaderGenericservice.stopSpinner();
        if (data) {
          this.optionsList = data;
          if (this.optionsList.length === 1) {
            this.revolvingQueriesForm.controls?.selectOption?.setValue(this.optionsList[0].id);
          }
        }
      })
      .catch((error) => {
        this.loaderGenericservice.stopSpinner();
        console.error(error);
      });
  }

  private getEstablishments() {
    this.combosStore
      .getEstablishments(this.getFilter())
      .then((data) => {
        this.loaderGenericservice.stopSpinner();
        if (data) {
          this.optionsList = data;
          if (this.optionsList.length === 1) {
            this.revolvingQueriesForm.controls?.selectOption?.setValue(this.optionsList[0].id);
          }
        }
      })
      .catch((error) => {
        this.loaderGenericservice.stopSpinner();
        console.error(error);
      });
  }

  private getFilter() {
    let filter: any = null;

    if (this.isMacroOffices || (this.isBussinesDirector && this.revolvingQueriesForm.get('macroOffice')?.value)) {
      filter = {
        officeId: this.revolvingQueriesForm.get('macroOffice')?.value,
        userType: OFFICE_DIRECTOR,
      };
    }

    if (this.isMacroChains || (this.isBussinesDirector && this.revolvingQueriesForm.get('macroChain')?.value)) {
      filter = {
        chainId: this.revolvingQueriesForm.get('macroChain')?.value,
        userType: CHAIN,
      };
    }

    return filter;
  }

  private getOptions() {
    this.revolvingStore
      .loadOperations()
      .then((data) => {
        if (data) {
          this.liquidationsList = data;
        }
      })
      .catch((error) => console.error(error));
  }

  private getTypes() {
    this.queryTypes = this.revolvingStore.getQueryTypes();
  }

  private getMacroChains() {
    if (this.isMacroChains) {
      return this.combosStore.getChainsFromMacroUser(this.userInfo.userId).then(
        (data) => {
          if (data) {
            this.macroChainList = data;
            if (this.macroChainList.length === 1) {
              this.revolvingQueriesForm.controls.officeChain.setValue(this.macroChainList[0].id);
            }
          }
        },
        (error) => console.error(error)
      );
    } else {
      return this.combosStore.getChainsByDivision(this.userInfo.division?.toString()).then(
        (data) => {
          if (data) {
            this.macroChainList = data;
            if (this.macroChainList.length === 1) {
              this.revolvingQueriesForm.controls.officeChain.setValue(this.macroChainList[0].id);
            }
            this.macroChainList.unshift({ id: '', value: '' });
          }
        },
        (error) => console.error(error)
      );
    }
  }
  private getMacroOffices() {
    if (this.isMacroOffices) {
      return this.combosStore.getOfficesFromMacroUser(this.userInfo.userId).then(
        (data) => {
          if (data) {
            this.macroOfficeList = data;
            if (this.macroOfficeList.length === 1) {
              this.revolvingQueriesForm.controls.officeChain.setValue(this.macroOfficeList[0].id);
            }
            this.macroOfficeList.unshift({ id: '', value: '' });
          }
        },
        (error) => console.error(error)
      );
    } else {
      return this.combosStore.getOfficesByDivision(this.userInfo.division?.toString()).then(
        (data) => {
          if (data) {
            this.macroOfficeList = data;
            if (this.macroOfficeList.length === 1) {
              this.revolvingQueriesForm.controls.officeChain.setValue(this.macroOfficeList[0].id);
            }
            this.macroOfficeList.unshift({ id: '', value: '' });
          }
        },
        (error) => console.error(error)
      );
    }
  }
}
